import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/function-management.css";
import { useAppContext } from "../context/AppContext";
import {
  createFunction,
  getFunctionsByCompany,
} from "../services/functionService";
import { getCompanyNames } from "../services/companyService";
import RichTextEditor from "../components/ui/RichTextEditor";
import ColorPicker from "../components/ui/ColorPicker";
import SideTabs from "../components/layout/SideTabs";
import { toast } from "../utils/toast";

const FunctionCreatePage = () => {
  const { setActiveSection } = useAppContext();
  const [form, setForm] = useState({
    name: "",
    function_code: "",
    background_color: "#A3A3A3",
    company_id: "",
    parent_id: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loadingFunctions, setLoadingFunctions] = useState(false);

  const loadFunctionsForCompany = async (companyId) => {
    if (!companyId) {
      setFunctions([]);
      setForm((s) => ({ ...s, parent_id: "" }));
      return;
    }
    try {
      setLoadingFunctions(true);
      const fn = await getFunctionsByCompany(companyId).catch(() => []);
      setFunctions(Array.isArray(fn) ? fn : []);
      setForm((s) => ({ ...s, parent_id: "" }));
    } finally {
      setLoadingFunctions(false);
    }
  };

  const didInit = useRef(false);
  useEffect(() => {
    (async () => {
      if (didInit.current) return; // StrictMode guard
      didInit.current = true;
      try {
        const cs = await getCompanyNames().catch(() => []);
        setCompanies(Array.isArray(cs) ? cs : []);
        // Don't auto-select first company - leave blank for user to select
        setFunctions([]);
      } catch {
        // leave empty but allow creation
      }
    })();
  }, []);

  const canSave = useMemo(
    () =>
      Boolean(
        String(form.name).trim() &&
          String(form.function_code).trim() &&
          String(form.company_id),
      ),
    [form.name, form.function_code, form.company_id],
  );

  const cancel = () => setActiveSection("function-management");
  const save = async () => {
    if (!canSave) {
      toast.error("Function Name, Function Code, and Company are required");
      return;
    }
    try {
      setSaving(true);
      await createFunction(form);
      toast.success(`Function "${form.name}" has been created successfully!`);
      // Mark that the management table should refresh when we return
      try {
        localStorage.setItem("functionsNeedsRefresh", "1");
      } catch {}
      // After successful creation, go back to Function Management table
      setActiveSection("function-management");
    } catch (e) {
      toast.error(e?.message || "Failed to create function");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Create Function</h2>
            <p className="page-subtitle">Create a new function entry</p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={cancel}>
              Cancel
            </button>
            <button
              className="primary-btn"
              onClick={save}
              disabled={saving || !canSave}
            >
              {saving ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <SideTabs
          defaultActiveId="basic"
          tabs={[
            {
              id: "basic",
              label: "Basic Details",
              content: (
                <div className="role-card">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Function Name *</label>
                      <input
                        value={form.name}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, name: e.target.value }))
                        }
                      />
                    </div>
                    <ColorPicker
                      label="Background Color"
                      value={form.background_color}
                      onChange={(hex) =>
                        setForm((s) => ({ ...s, background_color: hex }))
                      }
                    />
                    <div className="form-group">
                      <label>Function Code *</label>
                      <input
                        value={form.function_code}
                        onChange={(e) =>
                          setForm((s) => ({
                            ...s,
                            function_code: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Select Company *</label>
                      <select
                        value={form.company_id}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm((s) => ({ ...s, company_id: val }));
                          loadFunctionsForCompany(val);
                        }}
                      >
                        <option value="">Select Company</option>
                        {companies.map((c) => (
                          <option
                            key={c.company_id || c.id}
                            value={c.company_id || c.id}
                          >
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Parent Function</label>
                      <select
                        value={form.parent_id}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, parent_id: e.target.value }))
                        }
                        disabled={loadingFunctions}
                      >
                        <option value="">
                          {loadingFunctions ? "Loading..." : "None"}
                        </option>
                        {functions.map((f) => (
                          <option key={f.function_id} value={f.function_id}>
                            {f.name} 
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ),
            },
            {
              id: "description",
              label: "Description",
              content: (
                <div className="role-card">
                  <RichTextEditor
                    label="Function Overview"
                    value={form.description}
                    onChange={(html) =>
                      setForm((s) => ({ ...s, description: html }))
                    }
                    height={260}
                  />
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default FunctionCreatePage;
